import type { JSX, PropsWithChildren } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

/**
 * The demotion primitive: a small ⓘ that holds a claim's full sentence (spec ux-spec §6).
 *
 * Click, never hover — a caveat behind a hover does not exist on a phone, which is the codebase's
 * own argument and the reason this is a Popover rather than a Tooltip. The label is required
 * because the trigger's only visible content is an icon.
 */
export function InfoPopover({
  label,
  children,
}: PropsWithChildren<{ label: string }>): JSX.Element {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={label}
        // The tap target is bought in height, which costs the line nothing: 44px of width
        // centred on a 16px dot would swallow the words either side of a mid-sentence trigger.
        className="relative mx-1 inline-flex size-4 shrink-0 translate-y-px items-center justify-center rounded-full border border-line bg-surface align-middle font-sans text-[0.65rem] leading-none font-semibold text-ink-muted before:absolute before:top-1/2 before:left-1/2 before:h-11 before:w-7 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] hover:border-accent hover:text-accent focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:outline-none"
      >
        <span aria-hidden="true">i</span>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto max-w-[20rem] p-3 text-small leading-normal text-ink-secondary shadow-pop"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
